import React, { ReactNode } from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  RefreshCw,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  ChevronRight,
} from 'lucide-react';
import { AggregateEntityList } from '../../molecules/AggregateEntityList';
import { ControlReadinessIndicator, type ReadinessStatus } from '../../atoms/ControlReadinessIndicator';
import { type LikelihoodLevel } from '../../atoms/LikelihoodBadge';
import type { AffectedEntity, ControlEventAction } from '../../types';
import './EventTile.css';

export type EventType =
  | 'attention-required'
  | 'situation-updated'
  | 'obligation-due'
  | 'restriction-starts'
  | 'inspection-likelihood'
  | 'situation-resolved'
  | 'farm-updated';

export type EventPriority = 'urgent' | 'warning' | 'info' | 'success';

export interface EventTileProps {
  /** Unique event ID */
  id: string;
  /** Event type */
  type: EventType;
  /** Event title */
  title: string;
  /** Event subtitle/category */
  subtitle?: string;
  /** Event description */
  description?: string;
  /** Event timestamp */
  timestamp: Date | string;
  /** Priority level affects styling */
  priority?: EventPriority;
  /** Custom icon (overrides default based on type) */
  icon?: ReactNode;
  /** Whether the tile is expanded */
  isExpanded?: boolean;
  /** Toggle expand handler */
  onToggle?: () => void;
  /** Additional details shown when expanded */
  details?: ReactNode;
  /** Related entity link */
  relatedEntity?: {
    name: string;
    href?: string;
  };
  /** Optional class name */
  className?: string;
  /** Even/odd row for alternating background */
  isEvenRow?: boolean;

  // === Aggregate Event Support ===

  /** Whether this is an aggregate (container) event */
  isAggregate?: boolean;
  /** Hide the aggregate count badge (layers icon + number) */
  hideAggregateBadge?: boolean;
  /** List of affected entities for aggregate events */
  affectedEntities?: AffectedEntity[];
  /** Link to domain view for aggregate events */
  domainLink?: {
    label: string;
    href: string;
  };
  /** Available actions for this event */
  actions?: ControlEventAction[];
  /** Callback when an entity in aggregate list is clicked */
  onEntityClick?: (entityId: string) => void;
  /** Callback when the domain link is clicked (client-side navigation) */
  onDomainLinkClick?: () => void;
  /** Callback when an action is clicked */
  onActionClick?: (actionId: string) => void;

  // === Status Indicators ===

  /** Control readiness status (shows "Kontrolle bereit" badge) */
  controlReadiness?: {
    status: ReadinessStatus;
    metCount: number;
    totalCount: number;
    label: string;
  };
  /** Inspection likelihood level (shows likelihood badge) */
  inspectionLikelihood?: {
    level: LikelihoodLevel;
  };
}

/** Map event types to icons */
const EVENT_ICONS: Record<EventType, ReactNode> = {
  'attention-required': <AlertTriangle className="event-tile__icon-svg" />,
  'situation-updated': <RefreshCw className="event-tile__icon-svg" />,
  'obligation-due': <Calendar className="event-tile__icon-svg" />,
  'restriction-starts': <AlertCircle className="event-tile__icon-svg" />,
  'inspection-likelihood': <AlertTriangle className="event-tile__icon-svg" />,
  'situation-resolved': <CheckCircle className="event-tile__icon-svg" />,
  'farm-updated': <RefreshCw className="event-tile__icon-svg" />,
};

/** Map event types to default priorities */
const EVENT_PRIORITIES: Record<EventType, EventPriority> = {
  'attention-required': 'urgent',
  'situation-updated': 'info',
  'obligation-due': 'warning',
  'restriction-starts': 'warning',
  'inspection-likelihood': 'warning',
  'situation-resolved': 'success',
  'farm-updated': 'info',
};

/** Map event types to human-readable labels (German) */
const EVENT_LABELS: Record<EventType, string> = {
  'attention-required': 'Handlungsbedarf',
  'situation-updated': 'Aktualisierung',
  'obligation-due': 'Frist',
  'restriction-starts': 'Sperrfrist',
  'inspection-likelihood': 'Kontrollwahrscheinlichkeit',
  'situation-resolved': 'Erledigt',
  'farm-updated': 'Betriebsaktualisierung',
};

/** Format timestamp for display (German) */
function formatTimestamp(timestamp: Date | string): string {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffHours < 1) {
    const diffMins = Math.floor(diffMs / (1000 * 60));
    return `vor ${diffMins} Min.`;
  }
  if (diffHours < 24) {
    return `vor ${Math.floor(diffHours)} Std.`;
  }
  if (diffDays < 7) {
    return `vor ${Math.floor(diffDays)} Tag${Math.floor(diffDays) > 1 ? 'en' : ''}`;
  }

  return date.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
}

/**
 * EventTile - A card component for displaying events in the Workspace section.
 * Similar pattern to Document component - expandable with details.
 */
export const EventTile: React.FC<EventTileProps> = ({
  id: _id,
  type,
  title,
  subtitle,
  description,
  timestamp,
  priority,
  icon,
  isExpanded = false,
  onToggle,
  details,
  relatedEntity,
  className = '',
  isEvenRow = false,
  // Aggregate event support
  isAggregate = false,
  hideAggregateBadge: _hideAggregateBadge = false,
  affectedEntities,
  domainLink,
  actions,
  onEntityClick,
  onDomainLinkClick,
  onActionClick,
  // Status indicators
  controlReadiness,
  inspectionLikelihood: _inspectionLikelihood,
}) => {
  const effectivePriority = priority || EVENT_PRIORITIES[type];
  const effectiveIcon = icon || EVENT_ICONS[type];
  const typeLabel = EVENT_LABELS[type];

  return (
    <div
      className={`event-tile ${isEvenRow ? 'event-tile--even' : 'event-tile--odd'} ${isExpanded ? 'event-tile--expanded' : ''} ${className}`}
      data-priority={effectivePriority}
    >
      {/* Header - Always visible */}
      <div
        className={`event-tile__header ${isExpanded ? 'event-tile__header--expanded' : ''}`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle?.();
          }
        }}
      >
        <div className="event-tile__header-content">
          {/* Icon */}
          <div className={`event-tile__icon event-tile__icon--${effectivePriority}`}>
            {effectiveIcon}
          </div>

          {/* Main info */}
          <div className="event-tile__info">
            <div className="event-tile__title-row">
              <span className="event-tile__title">{title}</span>
              <span className={`event-tile__type event-tile__type--${effectivePriority}`}>
                {typeLabel}
              </span>
              {/* Status indicators */}
              {controlReadiness && (
                <ControlReadinessIndicator
                  status={controlReadiness.status}
                  label={controlReadiness.label}
                  size="sm"
                  variant="badge"
                  className="event-tile__readiness-badge"
                />
              )}
            </div>
          </div>

          {/* Expand indicator */}
          <ChevronRight
            className={`event-tile__chevron ${isExpanded ? 'event-tile__chevron--expanded' : ''}`}
          />
        </div>
      </div>

      {/* Expandable content */}
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{
          height: isExpanded ? 'auto' : 0,
          opacity: isExpanded ? 1 : 0,
        }}
        transition={{ duration: 0.2 }}
        className="event-tile__body"
      >
        <div className="event-tile__body-content">
          {/* Timestamp and subtitle - above both columns */}
          <div className="event-tile__timestamp event-tile__timestamp--expanded">
            <Clock className="event-tile__timestamp-icon" />
            {formatTimestamp(timestamp)}
            {subtitle && (
              <>
                <span className="event-tile__separator">|</span>
                <span className="event-tile__subtitle">{subtitle}</span>
              </>
            )}
          </div>

          {/* Two-column grid for aggregate events with details */}
          <div className={`event-tile__columns ${isAggregate && details ? 'event-tile__columns--two-column' : ''}`}>
            {/* Left column: Objects & Actions */}
            <div className="event-tile__column-primary">
              {description && (
                <p className="event-tile__description">{description}</p>
              )}

              {/* Aggregate Entity List for aggregate events */}
              {isAggregate && affectedEntities && affectedEntities.length > 0 && (
                <AggregateEntityList
                  entities={affectedEntities}
                  domainLink={domainLink}
                  onEntityClick={onEntityClick}
                  onDomainLinkClick={onDomainLinkClick}
                  className="event-tile__aggregate-list"
                />
              )}

              {/* Single-entity related link for non-aggregate events */}
              {!isAggregate && relatedEntity && (
                <div className="event-tile__related">
                  <span className="event-tile__related-label">Related:</span>
                  {relatedEntity.href ? (
                    <a href={relatedEntity.href} className="event-tile__related-link">
                      {relatedEntity.name}
                    </a>
                  ) : (
                    <span className="event-tile__related-name">{relatedEntity.name}</span>
                  )}
                </div>
              )}

              {/* Action buttons */}
              {actions && actions.length > 0 && (
                <div className="event-tile__actions">
                  {actions.map((action) => (
                    <button
                      key={action.id}
                      type="button"
                      className={`event-tile__action-button ${action.type === 'primary' ? 'event-tile__action-button--primary' : ''}`}
                      onClick={() => onActionClick?.(action.id)}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right column: Explanation (for aggregate events with details) */}
            {details && (
              <div className="event-tile__column-secondary">
                <div className="event-tile__details">{details}</div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EventTile;
